function _slicedToArray(r, e) { return _arrayWithHoles(r) || _iterableToArrayLimit(r, e) || _unsupportedIterableToArray(r, e) || _nonIterableRest(); }
function _nonIterableRest() { throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }
function _unsupportedIterableToArray(r, a) { if (r) { if ("string" == typeof r) return _arrayLikeToArray(r, a); var t = {}.toString.call(r).slice(8, -1); return "Object" === t && r.constructor && (t = r.constructor.name), "Map" === t || "Set" === t ? Array.from(r) : "Arguments" === t || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(t) ? _arrayLikeToArray(r, a) : void 0; } }
function _arrayLikeToArray(r, a) { (null == a || a > r.length) && (a = r.length); for (var e = 0, n = Array(a); e < a; e++) n[e] = r[e]; return n; }
function _iterableToArrayLimit(r, l) { var t = null == r ? null : "undefined" != typeof Symbol && r[Symbol.iterator] || r["@@iterator"]; if (null != t) { var e, n, i, u, a = [], f = !0, o = !1; try { if (i = (t = t.call(r)).next, 0 === l) { if (Object(t) !== t) return; f = !1; } else for (; !(f = (e = i.call(t)).done) && (a.push(e.value), a.length !== l); f = !0); } catch (r) { o = !0, n = r; } finally { try { if (!f && null != t.return && (u = t.return(), Object(u) !== u)) return; } finally { if (o) throw n; } } return a; } }
function _arrayWithHoles(r) { if (Array.isArray(r)) return r; }
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Navigate, useLocation } from 'react-router-dom';
import IdentifyPage from 'researcher/application/components/IdentifyPage';
import featureEnabled from 'shared/helper/feature';
import { T } from 'shared/helper/translate';
import { checkoutRequestFromSearch } from 'rubriq/helpers/emailCheckoutLink';
import { planLabel, pricesFromCheckout } from 'rubriq/helpers/emailCheckoutSummary';
import { ensurePaddleInitialised, paddleLocaleFor } from 'rubriq/helpers/paddleBilling';
import EmailCheckoutSummary from './EmailCheckoutSummary';
var STATUS = {
  LOADING: 'loading',
  OPEN: 'open',
  COMPLETED: 'completed',
  ERROR: 'error'
};
var OUTCOME_MESSAGES = {
  expired: 'This link has expired. Log in to Rubriq to add your payment method.',
  invalid: 'This link is not valid. Log in to Rubriq to add your payment method.',
  unavailable: 'There is nothing to pay for with this link. Your trial may have ended, or a payment method may already have been added.',
  already_subscribed: 'You are already subscribed to Rubriq Premium, so there is nothing more to pay.',
  failed: 'We could not open the checkout. Please try the link in your email again in a few minutes.'
};

/** Where Paddle draws its checkout: a class name, as Paddle.js expects. */
export var FRAME_TARGET = 'email-checkout-frame';

/**
 * The page Paddle's "default payment link" points at, and where the checkout link in a Rubriq
 * email ends up (`/:lang/rubriq/pay?_ptxn=txn_...`, with `&plan=...&interval=...` when the
 * checkout subscribes to a plan).
 *
 * It is public on purpose. The transaction in the URL was requested by megatron for one user,
 * so whoever completes the checkout - logged in or not, on whichever device they opened the
 * email - pays for that user and no other.
 *
 * Our own summary of the plan sits on the left ({@see EmailCheckoutSummary}); Paddle's checkout
 * is drawn inline on the right and only takes the card details. Seeing `_ptxn` in the URL,
 * Paddle.js opens that checkout by itself, so the settings are passed to `Paddle.Initialize()`
 * and the frame it draws into is on the page before then. `variant: 'one-page'` is not optional
 * - it is the only checkout Paddle supports for adding a payment method to a cardless trial.
 */
var EmailCheckoutPage = function EmailCheckoutPage() {
  var location = useLocation();
  var _useMemo = useMemo(function () {
      return checkoutRequestFromSearch(location.search);
    }, [location.search]),
    transactionId = _useMemo.transactionId,
    outcome = _useMemo.outcome,
    plan = _useMemo.plan,
    interval = _useMemo.interval;
  var _useSelector = useSelector(function (_ref) {
      var application = _ref.application;
      return application.paddle || {};
    }),
    scriptUrlv2 = _useSelector.scriptUrlv2,
    clientToken = _useSelector.clientToken,
    environment = _useSelector.environment;
  var siteLanguage = useSelector(function (_ref2) {
    var application = _ref2.application;
    return application.siteLanguage;
  });
  var locale = paddleLocaleFor(siteLanguage);
  var _useState = useState(STATUS.LOADING),
    _useState2 = _slicedToArray(_useState, 2),
    status = _useState2[0],
    setStatus = _useState2[1];
  var _useState3 = useState(null),
    _useState4 = _slicedToArray(_useState3, 2),
    prices = _useState4[0],
    setPrices = _useState4[1];
  // React StrictMode invokes effects twice in development; Paddle must only be set up once.
  var initialising = useRef(false);
  var enabled = featureEnabled('rubriq_cardless_free_trial');
  var checkoutSettings = useMemo(function () {
    return {
      displayMode: 'inline',
      variant: 'one-page',
      frameTarget: FRAME_TARGET,
      frameInitialHeight: 450,
      frameStyle: 'width: 100%; min-width: 312px; background-color: transparent; border: none;',
      locale: locale,
      theme: 'light'
    };
  }, [locale]);
  useEffect(function () {
    if (!enabled || !transactionId || initialising.current) {
      return;
    }
    initialising.current = true;
    ensurePaddleInitialised({
      scriptUrlv2: scriptUrlv2,
      clientToken: clientToken,
      environment: environment,
      checkoutSettings: checkoutSettings,
      eventCallback: function eventCallback(_ref3) {
        var name = _ref3.name,
          data = _ref3.data;
        if (name === 'checkout.loaded' || name === 'checkout.updated') {
          setPrices(function (current) {
            return pricesFromCheckout(data) || current;
          });
          setStatus(function (current) {
            return current === STATUS.LOADING ? STATUS.OPEN : current;
          });
        }
        if (name === 'checkout.completed') {
          var _window$Paddle;
          (_window$Paddle = window.Paddle) === null || _window$Paddle === void 0 || (_window$Paddle = _window$Paddle.Checkout) === null || _window$Paddle === void 0 || _window$Paddle.close();
          setStatus(STATUS.COMPLETED);
        }
        if (name === 'checkout.error') {
          setStatus(STATUS.ERROR);
        }
      }
    }).catch(function () {
      return setStatus(STATUS.ERROR);
    });
  }, [enabled, transactionId]);
  if (!enabled || !transactionId && !outcome) {
    return /*#__PURE__*/React.createElement(Navigate, {
      to: "/".concat(siteLanguage, "/rubriq"),
      replace: true
    });
  }
  var goToRubriq = function goToRubriq() {
    var primary = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : true;
    return (
      /*#__PURE__*/
      // A full page load rather than a router link: Paddle.js only honours its first
      // `Initialize()`, and the rest of Rubriq sets it up differently.
      React.createElement("a", {
        href: "/".concat(siteLanguage, "/rubriq"),
        className: "btn ".concat(primary ? 'btn-primary' : 'btn-outline-primary')
      }, /*#__PURE__*/React.createElement(T, {
        source: "Go to Rubriq"
      }))
    );
  };
  if (!transactionId || status === STATUS.COMPLETED) {
    return /*#__PURE__*/React.createElement("div", {
      className: "container py-5",
      "data-testid": "email-checkout-page"
    }, /*#__PURE__*/React.createElement(IdentifyPage, {
      robots: "noindex, nosnippet, noarchive",
      title: "Rubriq"
    }), /*#__PURE__*/React.createElement("div", {
      className: "row justify-content-center"
    }, /*#__PURE__*/React.createElement("div", {
      className: "col-md-8 col-lg-6 text-center"
    }, /*#__PURE__*/React.createElement("p", {
      className: "mb-4",
      role: "status"
    }, !transactionId && /*#__PURE__*/React.createElement(T, {
      source: OUTCOME_MESSAGES[outcome]
    }), transactionId && plan && /*#__PURE__*/React.createElement(T, {
      source: "You are now subscribed to %(plan)s. Thank you!",
      params: {
        plan: planLabel(plan)
      }
    }), transactionId && !plan && /*#__PURE__*/React.createElement(T, {
      source: "Your payment method has been added. Thank you!"
    })), goToRubriq())));
  }
  var reopenCheckout = function reopenCheckout() {
    var _window$Paddle2;
    if (!((_window$Paddle2 = window.Paddle) !== null && _window$Paddle2 !== void 0 && _window$Paddle2.Checkout)) {
      return;
    }
    setStatus(STATUS.LOADING);
    window.Paddle.Checkout.open({
      transactionId: transactionId,
      settings: checkoutSettings
    });
  };
  return /*#__PURE__*/React.createElement("div", {
    className: "container py-5",
    "data-testid": "email-checkout-page"
  }, /*#__PURE__*/React.createElement(IdentifyPage, {
    robots: "noindex, nosnippet, noarchive",
    title: "Rubriq"
  }), /*#__PURE__*/React.createElement("div", {
    className: "row g-4 justify-content-center"
  }, /*#__PURE__*/React.createElement("div", {
    className: "col-lg-5"
  }, /*#__PURE__*/React.createElement(EmailCheckoutSummary, {
    plan: plan,
    interval: interval,
    prices: prices,
    locale: locale
  })), /*#__PURE__*/React.createElement("div", {
    className: "col-lg-6"
  }, status === STATUS.ERROR && /*#__PURE__*/React.createElement("div", {
    className: "text-center",
    role: "status"
  }, /*#__PURE__*/React.createElement("p", {
    className: "mb-3"
  }, /*#__PURE__*/React.createElement(T, {
    source: "We could not open the checkout. Please try again."
  })), /*#__PURE__*/React.createElement("div", {
    className: "d-flex flex-wrap justify-content-center gap-2"
  }, /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "btn btn-primary",
    onClick: reopenCheckout
  }, /*#__PURE__*/React.createElement(T, {
    source: "Reopen checkout"
  })), goToRubriq(false))), status === STATUS.LOADING && /*#__PURE__*/React.createElement("div", {
    className: "text-center",
    role: "status"
  }, /*#__PURE__*/React.createElement("div", {
    className: "spinner-border text-primary",
    "aria-hidden": "true"
  }), /*#__PURE__*/React.createElement("p", {
    className: "mt-2"
  }, /*#__PURE__*/React.createElement(T, {
    source: "Loading checkout\u2026"
  }))), /*#__PURE__*/React.createElement("div", {
    className: FRAME_TARGET,
    "data-testid": "email-checkout-frame"
  }))));
};
export default EmailCheckoutPage;